import WaBadge from "@awesome.me/webawesome-pro/dist/react/badge/index.js";
import React from "react";

import { trackEvent } from "../analytics/plausible";

type Props = {
  title?: string;
  className?: string;
  isMobile?: boolean;
};

export const EstimateNotice: React.FC<Props> = ({
  title = "Taxi Fare Calculator",
  className,
  isMobile = false,
}) => {
  const badgeStyle: React.CSSProperties = {
    background: "rgba(59,130,246,0.12)",
    color: "rgba(30,64,175,1)",
    border: "1px solid rgba(59,130,246,0.25)",
  };

  return (
    <div>
      {" "}
      {/* Header row */}
      <div
        className="wa-cluster wa-gap-s"
        style={{
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <h2 className="wa-heading-s" style={{ margin: 0 }}>
          {title}
        </h2>

        <WaBadge style={badgeStyle}>Estimated fare</WaBadge>
      </div>
      {/* Description */}
      <div
        className="wa-body-s wa-color-neutral-700"
        style={{
          lineHeight: 1.65,
          marginBottom: 14,
        }}
      >
        This tool provides a quick taxi fare estimate for journeys that start within the{" "}
        <strong>Bournemouth, Christchurch and Poole (BCP) area</strong>. It uses the official{" "}
        <strong>BCP Council taxi tariff</strong> to give a consistent guide price based on the
        published tariff.
      </div>
      {/* Boundary & tariff note */}
      <div
        className="wa-body-s wa-color-neutral-600"
        style={{
          lineHeight: 1.6,
          marginBottom: 14,
        }}
      >
        Because the calculation uses council-mandated tariffs,{" "}
        <strong>the journey origin must be within the BCP area</strong>. You can enter destinations
        anywhere in the UK, although longer trips may be subject to operator discretion. The
        calculator will also indicate which <strong>BCP tariff</strong> your journey falls under.
      </div>
      {/* Callout */}
      <div
        className="wa-stack wa-gap-xs"
        role="note"
        aria-label="Important information about estimates"
        style={{
          padding: "14px 16px",
          borderRadius: 14,
          background: "rgba(15,23,42,0.03)",
          border: "1px solid rgba(15,23,42,0.10)",
        }}
      >
        <div className="wa-body-s wa-color-neutral-800" style={{ fontWeight: 700 }}>
          Important
        </div>

        <div className="wa-body-s wa-color-neutral-700" style={{ lineHeight: 1.6 }}>
          Fares shown are <strong>estimates</strong>, not guaranteed quotes. They’re calculated
          using the published tariff from the{" "}
          <a
            href="https://www.bcpcouncil.gov.uk/roads-and-travel/travel-and-transport/taxi-fares"
            target="_blank"
            rel="noreferrer noopener"
            style={{ fontWeight: 700 }}
            onClick={() => {
              try {
                trackEvent("tariff_source_clicked");
              } catch {
                // analytics should never block navigation
              }
            }}
          >
            BCP Council taxi fares page
          </a>
          , which many local operators use as a reference. Final pricing and any extras are set by
          the operator.
        </div>

        <div className="wa-body-s wa-color-neutral-700" style={{ lineHeight: 1.6 }}>
          If live traffic data is enabled, the estimate may include time-based delay charges. The
          route shown is an approximation and drivers may take a different route.
        </div>

        <div className="wa-body-s wa-color-neutral-700" style={{ lineHeight: 1.6 }}>
          While we can’t guarantee the final fare, route, or journey time in real-world conditions,
          this should give you a helpful indication of the likely cost.
        </div>

        <div className="wa-body-s wa-color-neutral-600" style={{ lineHeight: 1.6 }}>
          If you have any suggestions or want to leave a comment, you can share your feedback at the
          bottom of the page.
        </div>

        {isMobile && (
          <div className="wa-body-s wa-color-neutral-600" style={{ lineHeight: 1.6 }}>
            This site is not affiliated with BCP Council.
          </div>
        )}
      </div>
    </div>
  );
};
